import {State, Action, StateContext, Selector} from '@ngxs/store';
import {ISubcontractorState, ISubcontractor} from './types';
import {
    SetCurrentTab,
    SetFiltersBasedOnTab,
    SetSearchField,
    VerifyHMRC,
    VerifyVAT,
    FetchChangeRequestsNumber,
    SetCompanyID,
    ShowMore
} from './actions';
import {tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {IPagination} from '../../../../modules/authorization/store/types';
import {SubcontractorService} from './service';
import {patch, updateItem} from '@ngxs/store/operators';

const defaults: ISubcontractorState = {
    isLoading: false,
    currentTab: null,
    searchField: '',
    companyID: '',
    generalSubcontractorsFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        active: true
    },
    siteManagersFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        active: true,
        isSiteManager: true
    },
    inactiveSubcontractorsFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        active: false
    },
    changeRequestsSubcontractorsFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC'
    },
    removedSubcontractorsFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        deleted: true
    },
    subcontractorsCertificatesFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        deleted: true
    },
    generalSubcontractors: null,
    siteManagers: null,
    inactiveSubcontractors: null,
    changeRequestsSubcontractors: null,
    removedSubcontractors: null,
    subcontractorsCertificates: null,
    changeRequestsNumber: null
};

@State<ISubcontractorState>({
    name: 'subcontractor',
    defaults
})
@Injectable()
export class SubcontractorState {

    @Selector()
    static isLoading(state: ISubcontractorState) {
        return state.isLoading;
    }

    @Selector()
    static currentTab(state: ISubcontractorState) {
        return state.currentTab;
    }

    @Selector()
    static searchField(state: ISubcontractorState) {
        return state.searchField;
    }

    @Selector()
    static companyID(state: ISubcontractorState) {
        return state.companyID;
    }

    @Selector()
    static generalSubcontractorsFilters(state: ISubcontractorState) {
        return state.generalSubcontractorsFilters;
    }

    @Selector()
    static siteManagersFilters(state: ISubcontractorState) {
        return state.siteManagersFilters;
    }

    @Selector()
    static inactiveSubcontractorsFilters(state: ISubcontractorState) {
        return state.inactiveSubcontractorsFilters;
    }

    @Selector()
    static changeRequestsSubcontractorsFilters(state: ISubcontractorState) {
        return state.changeRequestsSubcontractorsFilters;
    }

    @Selector()
    static removedSubcontractorsFilters(state: ISubcontractorState) {
        return state.removedSubcontractorsFilters;
    }
    @Selector()
    static subcontractorsCertificatesFilters(state: ISubcontractorState) {
        return state.subcontractorsCertificatesFilters;
    }

    @Selector()
    static generalSubcontractors(state: ISubcontractorState) {
        return state.generalSubcontractors;
    }

    @Selector()
    static siteManagers(state: ISubcontractorState) {
        return state.siteManagers;
    }

    @Selector()
    static inactiveSubcontractors(state: ISubcontractorState) {
        return state.inactiveSubcontractors;
    }

    @Selector()
    static changeRequestsSubcontractors(state: ISubcontractorState) {
        return state.changeRequestsSubcontractors;
    }

    @Selector()
    static removedSubcontractors(state: ISubcontractorState) {
        return state.removedSubcontractors;
    }

    @Selector()
    static subcontractorsCertificates(state: ISubcontractorState) {
        return state.subcontractorsCertificates;
    }

    @Selector()
    static changeRequestsNumber(state: ISubcontractorState) {
        return state.changeRequestsNumber;
    }

    constructor(private subcontractorService: SubcontractorService) {
    }

    @Action(SetCurrentTab)
    SetCurrentTab(ctx: StateContext<ISubcontractorState>, action: SetCurrentTab) {
        const state = ctx.getState();
        ctx.patchState({currentTab: action.tab || state.currentTab || 'GENERAL'});
        ctx.dispatch(new SetFiltersBasedOnTab({page: 1, ...action.filters}));
    }

    @Action(SetSearchField)
    SetSearchField(ctx: StateContext<ISubcontractorState>, action: SetSearchField) {
        ctx.patchState({searchField: action.value});
        ctx.dispatch(new SetFiltersBasedOnTab({page: 1}));
    }

    @Action(SetCompanyID)
    SetCompanyID(ctx: StateContext<ISubcontractorState>, action: SetCompanyID) {
        ctx.patchState({companyID: action.value ? action.value : ''});
        ctx.dispatch(new SetFiltersBasedOnTab({page: 1}));
    }

    @Action(SetFiltersBasedOnTab)
    SetFiltersBasedOnTab(ctx: StateContext<ISubcontractorState>, action: SetFiltersBasedOnTab) {
        let state = ctx.getState();
        ctx.patchState({isLoading: true});
        switch (state.currentTab) {
            case 'SITE MANAGERS':
                ctx.patchState({
                    siteManagersFilters: {
                        ...state.siteManagersFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.subcontractorService.fetchAll({
                    ...state.siteManagersFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        siteManagers: pagination,
                        isLoading: false
                    })));
            case 'INACTIVE':
                ctx.patchState({
                    inactiveSubcontractorsFilters: {
                        ...state.inactiveSubcontractorsFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.subcontractorService.fetchAll({
                    ...state.inactiveSubcontractorsFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        inactiveSubcontractors: pagination,
                        isLoading: false
                    })));
            case 'CHANGE REQUESTS':
                ctx.patchState({
                    changeRequestsSubcontractorsFilters: {
                        ...state.changeRequestsSubcontractorsFilters,
                        ...action.filters
                    }
                });
                ctx.dispatch(new FetchChangeRequestsNumber());
                state = ctx.getState();
                return this.subcontractorService.fetchAllRequests({
                    ...state.changeRequestsSubcontractorsFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        changeRequestsSubcontractors: pagination,
                        isLoading: false
                    })));
            case 'REMOVED':
                ctx.patchState({
                    removedSubcontractorsFilters: {
                        ...state.removedSubcontractorsFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.subcontractorService.fetchAll({
                    ...state.removedSubcontractorsFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        removedSubcontractors: pagination,
                        isLoading: false
                    })));
            case 'EXPIRY REMINDER':
                ctx.patchState({
                    subcontractorsCertificatesFilters: {
                        ...state.subcontractorsCertificatesFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.subcontractorService.getSubcontractorsCertificates({
                    ...state.subcontractorsCertificatesFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        subcontractorsCertificates: pagination,
                        isLoading: false
                    })));
            default:
                ctx.patchState({
                    generalSubcontractorsFilters: {
                        ...state.generalSubcontractorsFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.subcontractorService.fetchAll({
                    ...state.generalSubcontractorsFilters,
                    search: state.searchField,
                    companyID: state.companyID
                })
                    .pipe(tap((pagination: IPagination<ISubcontractor>) => ctx.patchState({
                        generalSubcontractors: pagination,
                        isLoading: false
                    })));
        }
    }

    @Action(VerifyHMRC)
    VerifyHMRC(ctx: StateContext<ISubcontractorState>, action: VerifyHMRC) {
        return this.subcontractorService.verifyHMRC(action.subcontractorID)
            .pipe(
                tap(() => ctx.setState(
                    patch({
                        generalSubcontractors: patch({
                            results: updateItem<ISubcontractor>(el => el._id === action.subcontractorID, patch({
                                hmrc_is_in_progress: Boolean(true)
                            }))
                        })
                    })
                ))
            );
    }

    @Action(VerifyVAT)
    VerifyVAT(ctx: StateContext<ISubcontractorState>, action: VerifyVAT) {
        return this.subcontractorService.verifyVAT(action.subcontractorID)
            .pipe(
                tap(() => ctx.setState(
                    patch({
                        generalSubcontractors: patch({
                            results: updateItem<ISubcontractor>(el => el._id === action.subcontractorID, patch({
                                vat: patch({
                                    is_in_progress: Boolean(true)
                                })
                            }))
                        })
                    })
                ))
            );
    }

    @Action(FetchChangeRequestsNumber)
    FetchChangeRequestsNumber(ctx: StateContext<ISubcontractorState>) {
        return this.subcontractorService.fetchAllChangeRequestsNumber()
            .pipe(tap((amount: number) => ctx.patchState({changeRequestsNumber: amount})));
    }

    @Action(ShowMore)
    ShowMore(ctx: StateContext<ISubcontractorState>, action: ShowMore) {
        const state = ctx.getState();
        const siteManager: ISubcontractor = state.siteManagers.results.find((el: ISubcontractor) => el._id === action.siteManager._id);
        if (!siteManager.assignedSubcontractors) {
            return this.subcontractorService.fetchAllAssigned(action.siteManager._id, { page: 1, results: 999, sort: 'fullnameDESC' })
                .pipe(
                    tap(
                        (data: IPagination) => {
                            ctx.setState(
                                patch({
                                    siteManagers: patch({
                                        results: updateItem<ISubcontractor>(el => el._id === action.siteManager._id, {
                                            ...siteManager,
                                            assignedSubcontractors: data.results,
                                            isOpened: true
                                        })
                                    })
                                })
                            );
                        }
                    )
                );
        } else {
            return ctx.setState(
                patch({
                    siteManagers: patch({
                        results: updateItem<ISubcontractor>(el => el._id === action.siteManager._id, { ...siteManager, isOpened: !siteManager.isOpened })
                    })
                })
            );
        }
    }

}
